import { asArray, asString } from 'ol/color';
import { Style, Stroke, Fill, RegularShape, Circle as StyleCircle, Icon as StyleIcon } from 'ol/style';
import type { Geometry } from 'ol/geom';
import { LineString, Point, Polygon } from 'ol/geom';
import type { Options as IconOptions } from 'ol/style/Icon';
import Icon from 'ol/style/Icon';
import type { Options as CircleOptions } from 'ol/style/Circle';
import type { Options as RegularShapeOptions } from 'ol/style/RegularShape';
import type { Options as StrokeOptions } from 'ol/style/Stroke';
import type { Options as FillOptions } from 'ol/style/Fill';
import type { FeatureLike } from 'ol/Feature';
import type Feature from 'ol/Feature';
import { toContext } from 'ol/render';

import { setAlphaColor } from '@/core/utils/utilities';
import { DateMgt } from '@/core/utils/date-mgt';
import type {
  TypeLayerStyleConfigType,
  TypeFillStyle,
  TypePolygonVectorConfig,
  TypeIconSymbolVectorConfig,
  TypeLineStyle,
  TypeLineStringVectorConfig,
  TypeSimpleSymbolVectorConfig,
  TypeSymbol,
  TypeKindOfVectorSettings,
  TypeStyleGeometry,
  TypeLayerStyleSettings,
  TypeLayerStyleConfig,
  TypeLayerStyleConfigInfo,
  TypeLayerStyleValueCondition,
  TypeAliasLookup,
  codedValueType,
} from '@/api/types/map-schema-types';
import {
  isFilledPolygonVectorConfig,
  isIconSymbolVectorConfig,
  isLineStringVectorConfig,
  isSimpleSymbolVectorConfig,
} from '@/api/types/map-schema-types';
import type { TypeLayerMetadataFields } from '@/api/types/layer-schema-types';
import type { FillPatternLine, FillPatternSettings, FilterNodeType } from './geoview-renderer-types';
import { binaryKeywors, defaultColor, groupKeywords, NodeType, operatorPriority, unaryKeywords } from './geoview-renderer-types';
import type { TypeVectorLayerStyles } from '@/geo/layer/geoview-layers/abstract-geoview-layers';
import { logger } from '@/core/utils/logger';
import { NotSupportedError } from '@/core/exceptions/core-exceptions';

type TypeStyleProcessor = (
  styleSettings: TypeLayerStyleSettings | TypeKindOfVectorSettings,
  feature?: Feature,
  filterEquation?: FilterNodeType[],
  legendFilterIsOff?: boolean,
  domainsLookup?: TypeLayerMetadataFields[],
  aliasLookup?: TypeAliasLookup
) => Style | undefined;

/** Default value of the legend canvas width when the settings do not provide one. */
const LEGEND_CANVAS_WIDTH = 50;

/** Default value of the legend canvas height when the settings do not provide one. */
const LEGEND_CANVAS_HEIGHT = 50;

let colorCount = 0;

export abstract class GeoviewRenderer {
  /**
   * Get the default color using the default color index.
   *
   * @param {number} alpha - Alpha value to associate to the color.
   * @param {boolean} increment - True, if we want to skip to next color
   *
   * @returns {string} The current default color string.
   */
  // TODO: create a mechanism to have one counter by map if needed with a small class who reuse the static function
  static getDefaultColor(alpha: number, increment: boolean = false): string {
    // get color then increment if needed
    const color = asString(setAlphaColor(asArray(defaultColor[colorCount]), alpha));
    if (increment) colorCount++;
    return color;
  }

  /**
   * This method returns the type of geometry. It removes the Multi prefix because for the geoviewRenderer, a MultiPoint has
   * the same behaviour than a Point.
   *
   * @param {FeatureLike} feature - The feature to check
   *
   * @returns {TypeStyleGeometry} The type of geometry (Point, LineString, Polygon).
   */
  static getGeometryType(feature: FeatureLike): TypeStyleGeometry {
    const geometryType = feature.getGeometry()?.getType();
    if (!geometryType) throw new Error('Features must have a geometry type.');
    return (geometryType.startsWith('Multi') ? geometryType.slice(5) : geometryType) as TypeStyleGeometry;
  }

  /**
   * Decodes a base64-encoded SVG string and replaces parameterized placeholders
   * (e.g., `param(fill)` or `param(outline)`) with actual values provided
   * as query parameters appended to the base64 string.
   * This is particularly useful for decoding and normalizing SVG symbols
   * exported from QGIS, which may include dynamic styling parameters such as
   * `fill`, `stroke`, or `outline` values.
   * The method also applies various cleanup steps to improve SVG compatibility:
   * - Fixes QGIS-specific attribute spacing (e.g. `"stroke="` issues)
   * - Corrects malformed opacity or width attributes
   * - Removes extraneous `<title>`, `<desc>`, `<defs>` tags
   * - Removes XML headers that can cause encoding errors
   * @param {string} base64 - The base64-encoded SVG string, optionally including
   *   query parameters (e.g. `"base64:...?...fill=%23ff0000&outline=%23000000"`).
   * @returns {string} The decoded, cleaned, and parameter-substituted SVG XML string.
   */
  static base64ToSVGString(base64: string): string {
    if (!base64) return base64;

    // Split the parameters (e.g. ?fill=...&outline=...)
    const [basePart, queryString] = base64.split('?');
    const b64 = basePart.replace(/^base64:/, '');
    let svgText = window.atob(b64);

    // Decode and parse the parameters
    const params = new URLSearchParams(queryString ?? '');
    for (const [key, value] of params.entries()) {
      // For example: replace param(fill) with "#000000"
      const decodedVal = decodeURIComponent(value);
      svgText = svgText.replace(new RegExp(`param\\(${key}\\)`, 'g'), decodedVal);
    }

    // Fix QGIS stroke property not being spaced correctly...
    svgText = svgText.replace('"stroke=', '" stroke=');

    // Fix QGIS stroke-opacity/fill-opacity having wrong values...
    svgText = svgText.replace(/(stroke-opacity|fill-opacity|stroke-width)="([\d.]+)\s+[\d.]+"/g, '$1="$2"');

    // Replace extra QGIS meta stuff
    svgText = svgText
      .replace(/<title>.*?<\/title>/gi, '')
      .replace(/<desc>.*?<\/desc>/gi, '')
      .replace(/<defs>\s*<\/defs>/gi, '')
      .replace(/\s{2,}/g, ' ');

    // Remove XML header if present (can cause EncodingError)
    svgText = svgText.replace(/<\?xml[^>]*>\s*/i, '');

    // Return final
    return svgText.trim();
  }

  /**
   * Encodes an SVG XML string into a base64-encoded string.
   * This is the inverse of {@link base64ToSVGString}, allowing you to safely
   * embed or transmit SVG data in formats where raw XML is not permitted.
   * @param {string} svgXML - The raw SVG XML string to encode.
   * @returns {string} A base64-encoded representation of the SVG string.
   */
  static SVGStringToBase64(svgXML: string): string {
    return window.btoa(svgXML);
  }

  /**
   * This method loads the image of an icon that compose the legend.
   *
   * @param {string} src - Source information (base64 image) of the image to load.
   *
   * @returns {Promise<HTMLImageElement>} A promise that the image is loaded.
   */
  static loadImage(src: string): Promise<HTMLImageElement | null> {
    const promisedImage = new Promise<HTMLImageElement | null>((resolve) => {
      const image = new Image();
      image.src = src;
      image
        .decode()
        .then(() => resolve(image))
        .catch((error: unknown) => {
          logger.logError('this.loadImage(src) - Error while loading the src image =', src, error);
          resolve(null);
        });
    });
    return promisedImage;
  }

  /**
   * This method creates a canvas with the image of an icon that is defined in the point style.
   *
   * @param {Style} pointStyle - Style associated to the point symbol.
   *
   * @returns {Promise<HTMLCanvasElement>} A promise that the canvas is created.
   */
  static async createIconCanvas(pointStyle?: Style): Promise<HTMLCanvasElement | null> {
    try {
      const iconStyle = pointStyle?.getImage() as Icon;
      const image = await this.loadImage(iconStyle.getSrc()!);
      if (image) {
        const size = iconStyle.getSize();
        const width = Array.isArray(size) ? size[0] : image.width || LEGEND_CANVAS_WIDTH;
        const height = Array.isArray(size) ? size[1] : image.height || LEGEND_CANVAS_HEIGHT;
        const drawingCanvas = document.createElement('canvas');
        drawingCanvas.width = width;
        drawingCanvas.height = height;
        const drawingContext = drawingCanvas.getContext('2d', { willReadFrequently: true })!;
        drawingContext.globalAlpha = iconStyle.getOpacity();
        drawingContext.drawImage(image, 0, 0);
        return drawingCanvas;
      }
      return null;
    } catch (error: unknown) {
      logger.logError(`Error creating incon canvas for pointStyle`, error);
      return null;
    }
  }

  // #region CREATE CANVAS

  /**
   * This method creates a canvas with the vector point settings that are defined in the point style.
   *
   * @param {Style} pointStyle - Style associated to the point symbol.
   *
   * @returns {Promise<HTMLCanvasElement>} A promise that the canvas is created.
   */
  static createPointCanvas(pointStyle?: Style): HTMLCanvasElement {
    const size = pointStyle?.getImage()?.getSize();
    const [width, height] = Array.isArray(size) ? size : [LEGEND_CANVAS_WIDTH, LEGEND_CANVAS_HEIGHT];
    const drawingCanvas = document.createElement('canvas');
    drawingCanvas.width = width + 4;
    drawingCanvas.height = height + 4;
    const drawingContext = toContext(drawingCanvas.getContext('2d', { willReadFrequently: true })!);
    if (pointStyle) drawingContext.setStyle(pointStyle);
    drawingContext.setTransform([1, 0, 0, 1, 0, 0]);
    drawingContext.drawGeometry(new Point([drawingCanvas.width / 2, drawingCanvas.width / 2]));
    return drawingCanvas;
  }

  /**
   * This method creates a canvas with the lineString settings that are defined in the style.
   *
   * @param {Style} lineStringStyle - Style associated to the lineString.
   *
   * @returns {Promise<HTMLCanvasElement>} A promise that the canvas is created.
   */
  static createLineStringCanvas(lineStringStyle?: Style): HTMLCanvasElement {
    const drawingCanvas = document.createElement('canvas');
    drawingCanvas.width = LEGEND_CANVAS_WIDTH;
    drawingCanvas.height = LEGEND_CANVAS_HEIGHT;
    const context = drawingCanvas.getContext('2d', { willReadFrequently: true })!;
    const gradient = context.createLinearGradient(0, drawingCanvas.height, drawingCanvas.width, 0);
    gradient.addColorStop(0, '#7f7f7f');
    gradient.addColorStop(0.667, '#ffffff');
    gradient.addColorStop(1, '#ffffff');
    context.fillStyle = gradient;
    context.fillRect(0, 0, drawingCanvas.width, drawingCanvas.height);
    const drawingContext = toContext(context);
    drawingContext.setStyle(lineStringStyle!);
    drawingContext.setTransform([1, 0, 0, 1, 0, 0]);
    drawingContext.drawGeometry(
      new LineString([
        [4, drawingCanvas.height - 4],
        [drawingCanvas.width - 4, 4],
      ])
    );
    return drawingCanvas;
  }

  /**
   * This method creates a canvas with the polygon settings that are defined in the style.
   *
   * @param {Style} polygonStyle - Style associated to the polygon.
   *
   * @returns {Promise<HTMLCanvasElement>} A promise that the canvas is created.
   */
  static createPolygonCanvas(polygonStyle?: Style): HTMLCanvasElement {
    const drawingCanvas = document.createElement('canvas');
    drawingCanvas.width = LEGEND_CANVAS_WIDTH;
    drawingCanvas.height = LEGEND_CANVAS_HEIGHT;
    const context = drawingCanvas.getContext('2d', { willReadFrequently: true })!;
    const gradient = context.createLinearGradient(0, drawingCanvas.height, drawingCanvas.width, 0);
    gradient.addColorStop(0, '#7f7f7f');
    gradient.addColorStop(0.667, '#ffffff');
    gradient.addColorStop(1, '#ffffff');
    context.fillStyle = gradient;
    context.fillRect(0, 0, drawingCanvas.width, drawingCanvas.height);
    const drawingContext = toContext(context);
    drawingContext.setStyle(polygonStyle!);
    drawingContext.setTransform([1, 0, 0, 1, 0, 0]);
    drawingContext.drawGeometry(
      new Polygon([
        [
          [4, 4],
          [drawingCanvas.width - 4, 4],
          [drawingCanvas.width - 4, drawingCanvas.height - 4],
          [4, drawingCanvas.height - 4],
          [4, 4],
        ],
      ])
    );

    context.getImageData(0, 0, drawingCanvas.width, drawingCanvas.height);
    return drawingCanvas;
  }

  // #endregion CREATE CANVAS

  /**
   * Create the stroke options using the specified settings.
   *
   * @param {TypeSimpleSymbolVectorConfig | TypeLineStringVectorConfig | TypePolygonVectorConfig} settings - Settings to use
   * for the stroke options creation.
   *
   * @returns {StrokeOptions} The stroke options created.
   */
  static createStrokeOptions(settings: TypeSimpleSymbolVectorConfig | TypeLineStringVectorConfig | TypePolygonVectorConfig): StrokeOptions {
    // eslint-disable-next-line no-param-reassign
    if (settings.stroke === undefined) settings.stroke = {};
    if (settings.stroke.color === undefined) {
      if ('color' in settings)
        // eslint-disable-next-line no-param-reassign
        settings.stroke.color = asString(setAlphaColor(asArray((settings as TypeSimpleSymbolVectorConfig).color!), 1));
      // eslint-disable-next-line no-param-reassign
      else settings.stroke.color = this.getDefaultColor(1, true);
    }

    const strokeOptions: StrokeOptions = {
      color: settings.stroke?.color,
      width: settings.stroke?.width,
      lineCap: settings.stroke?.lineCap || 'round',
      lineJoin: settings.stroke?.lineJoin || 'bevel',
      lineDash:
        settings.stroke?.lineDash || this.lineDashSettings[settings.stroke?.lineStyle !== undefined ? settings.stroke?.lineStyle : 'solid'],
    };
    return strokeOptions;
  }

  /**
   * Execute an operator using the nodes on the data stack. The filter equation is evaluated using a postfix notation. The result
   * is pushed back on the data stack. If a problem is detected, an error object is thrown with an explanatory message.
   *
   * @param {FilterNodeType} operator - Operator to execute.
   * @param {FilterNodeType[]} dataStack - Data stack to use for the operator execution.
   */
  static executeOperator(operator: FilterNodeType, dataStack: FilterNodeType[]): void {
    if (operator.nodeType === NodeType.binary) {
      if (dataStack.length < 2 || dataStack[dataStack.length - 2].nodeValue === '(')
        throw new Error(`binary operator error - operator = '${operator.nodeValue}'`);
      else {
        const operand2 = dataStack.pop()!;
        const operand1 = dataStack.pop()!;
        let valueToPush;
        switch (operator.nodeValue) {
          case 'is not':
            if (operand2.nodeValue !== null) throw new Error(`Invalid is not null operator syntax`);
            dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue !== null });
            break;
          case 'is':
            if (operand2.nodeValue !== null) throw new Error(`Invalid is null operator syntax`);
            dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue === null });
            break;
          case '=':
            if (operand1.nodeValue === null || operand2.nodeValue === null)
              dataStack.push({ nodeType: NodeType.variable, nodeValue: null });
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue === operand2.nodeValue });
            break;
          case '<':
            if (operand1.nodeValue === null || operand2.nodeValue === null)
              dataStack.push({ nodeType: NodeType.variable, nodeValue: null });
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue < operand2.nodeValue });
            break;
          case '>':
            if (operand1.nodeValue === null || operand2.nodeValue === null)
              dataStack.push({ nodeType: NodeType.variable, nodeValue: null });
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue > operand2.nodeValue });
            break;
          case '<=':
            if (operand1.nodeValue === null || operand2.nodeValue === null)
              dataStack.push({ nodeType: NodeType.variable, nodeValue: null });
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue <= operand2.nodeValue });
            break;
          case '>=':
            if (operand1.nodeValue === null || operand2.nodeValue === null)
              dataStack.push({ nodeType: NodeType.variable, nodeValue: null });
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue >= operand2.nodeValue });
            break;
          case '<>':
            if (operand1.nodeValue === null || operand2.nodeValue === null)
              dataStack.push({ nodeType: NodeType.variable, nodeValue: null });
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue !== operand2.nodeValue });
            break;
          case 'and':
            if (
              (operand1.nodeValue === null && (operand2.nodeValue === null || operand2.nodeValue === true)) ||
              (operand1.nodeValue === true && operand2.nodeValue === null)
            )
              dataStack.push({ nodeType: NodeType.variable, nodeValue: null });
            else if (
              (operand1.nodeValue === null && operand2.nodeValue === false) ||
              (operand1.nodeValue === false && operand2.nodeValue === null)
            )
              dataStack.push({ nodeType: NodeType.variable, nodeValue: false });
            else if (typeof operand1.nodeValue !== 'boolean' || typeof operand2.nodeValue !== 'boolean')
              throw new Error(`and operator error`);
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue && operand2.nodeValue });
            break;
          case 'or':
            if (
              (operand1.nodeValue === null && (operand2.nodeValue === null || operand2.nodeValue === false)) ||
              (operand1.nodeValue === false && operand2.nodeValue === null)
            )
              dataStack.push({ nodeType: NodeType.variable, nodeValue: null });
            else if (
              (operand1.nodeValue === null && operand2.nodeValue === true) ||
              (operand1.nodeValue === true && operand2.nodeValue === null)
            )
              dataStack.push({ nodeType: NodeType.variable, nodeValue: true });
            else if (typeof operand1.nodeValue !== 'boolean' || typeof operand2.nodeValue !== 'boolean')
              throw new Error(`or operator error`);
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue || operand2.nodeValue });
            break;
          case '+':
            if (typeof operand1.nodeValue !== 'number' || typeof operand2.nodeValue !== 'number') throw new Error(`+ operator error`);
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue + operand2.nodeValue });
            break;
          case '-':
            if (typeof operand1.nodeValue !== 'number' || typeof operand2.nodeValue !== 'number') throw new Error(`- operator error`);
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue - operand2.nodeValue });
            break;
          case '*':
            if (typeof operand1.nodeValue !== 'number' || typeof operand2.nodeValue !== 'number') throw new Error(`* operator error`);
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue * operand2.nodeValue });
            break;
          case '/':
            if (typeof operand1.nodeValue !== 'number' || typeof operand2.nodeValue !== 'number') throw new Error(`/ operator error`);
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand1.nodeValue / operand2.nodeValue });
            break;
          case '||':
            if ((typeof operand1.nodeValue !== 'string' && operand1.nodeValue !== null) || typeof operand2.nodeValue !== 'string')
              throw new Error(`|| operator error`);
            else
              dataStack.push({
                nodeType: NodeType.variable,
                nodeValue: operand1.nodeValue === null ? null : `${operand1.nodeValue}${operand2.nodeValue}`,
              });
            break;
          case 'like':
            if ((typeof operand1.nodeValue !== 'string' && operand1.nodeValue !== null) || typeof operand2.nodeValue !== 'string')
              throw new Error(`like operator error`);
            else {
              const regularExpression = new RegExp(
                operand2.nodeValue.toLowerCase().replaceAll('.', '\\.').replaceAll('%', '.*').replaceAll('_', '.'),
                ''
              );
              const match = operand1.nodeValue ? operand1.nodeValue.toLowerCase().match(regularExpression) : null;
              dataStack.push({ nodeType: NodeType.variable, nodeValue: match !== null && match[0] === operand1.nodeValue?.toLowerCase() });
            }
            break;
          case ',':
            valueToPush = {
              nodeType: NodeType.variable,
              nodeValue: Array.isArray(operand2.nodeValue)
                ? ([operand1.nodeValue].concat(operand2.nodeValue) as string[] | number[])
                : ([operand1.nodeValue, operand2.nodeValue] as string[] | number[]),
            };
            if (typeof valueToPush.nodeValue[0] !== typeof valueToPush.nodeValue[1]) throw new Error(`IN clause can't mix types`);
            dataStack.push(valueToPush);
            break;
          case 'in':
            if (Array.isArray(operand2.nodeValue))
              dataStack.push({
                nodeType: NodeType.variable,
                nodeValue: operand2.nodeValue.includes(operand1.nodeValue as never),
              });
            else
              dataStack.push({
                nodeType: NodeType.variable,
                nodeValue: operand1.nodeValue === operand2.nodeValue,
              });
            break;
          default:
            throw new Error(`unknown operator error`);
        }
      }
      return;
    }

    if (operator.nodeType === NodeType.unary) {
      if (dataStack.length < 1 || dataStack[dataStack.length - 1].nodeValue === '(') throw new Error(`unary operator error`);
      else {
        const operand = dataStack.pop()!;
        switch (operator.nodeValue) {
          case 'not':
            if (typeof operand.nodeValue !== 'boolean') throw new Error(`not operator error`);
            dataStack.push({ nodeType: NodeType.variable, nodeValue: !operand.nodeValue });
            break;
          case 'u-':
            if (typeof operand.nodeValue !== 'number') throw new Error(`unary - operator error`);
            dataStack.push({ nodeType: NodeType.variable, nodeValue: -operand.nodeValue });
            break;
          case 'u+':
            if (typeof operand.nodeValue !== 'number') throw new Error(`unary + operator error`);
            dataStack.push({ nodeType: NodeType.variable, nodeValue: operand.nodeValue });
            break;
          case 'date':
            if (operand.nodeValue === null) dataStack.push(operand);
            else if (typeof operand.nodeValue !== 'string') throw new Error(`DATE operator error`);
            else {
              operand.nodeValue = DateMgt.applyInputDateFormat(operand.nodeValue);
              dataStack.push({
                nodeType: NodeType.variable,
                nodeValue: DateMgt.convertToMilliseconds(DateMgt.convertToUTC(operand.nodeValue)),
              });
            }
            break;
          case 'upper':
            if (operand.nodeValue === null) dataStack.push(operand);
            else if (typeof operand.nodeValue !== 'string') throw new Error(`UPPER operator error`);
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand.nodeValue.toUpperCase() });
            break;
          case 'lower':
            if (operand.nodeValue === null) dataStack.push(operand);
            else if (typeof operand.nodeValue !== 'string') throw new Error(`LOWER operator error`);
            else dataStack.push({ nodeType: NodeType.variable, nodeValue: operand.nodeValue.toLowerCase() });
            break;
          default:
            throw new NotSupportedError(`unknown operator error`);
        }
      }
    }
  }

  /**
   * Use the filter equation and the feature fields to determine if the feature is visible.
   *
   * @param {Feature} feature - Feature used to find the visibility value to return.
   * @param {FilterNodeType[]} filterEquation - Filter used to find the visibility value to return.
   *
   * @returns {boolean | undefined} The visibility flag for the feature specified.
   */
  static featureIsNotVisible(feature: Feature, filterEquation: FilterNodeType[]): boolean | undefined {
    const operatorStack: FilterNodeType[] = [];
    const dataStack: FilterNodeType[] = [];

    const operatorAt = (index: number, stack: FilterNodeType[]): FilterNodeType | undefined => {
      if (index < 0 && stack.length + index >= 0) return stack[stack.length + index];
      if (index > 0 && index < stack.length) return stack[index];
      return undefined;
    };

    const findPriority = (target: FilterNodeType): number => {
      const i = operatorPriority.findIndex((element) => element.key === target.nodeValue);
      if (i === -1) return -1;
      return operatorPriority[i].priority;
    };

    try {
      for (let i = 0; i < filterEquation.length; i++) {
        if (filterEquation[i].nodeType === NodeType.variable) {
          const fieldValue = feature.get(filterEquation[i].nodeValue as string);
          dataStack.push({ nodeType: NodeType.variable, nodeValue: fieldValue || null });
        } else if ([NodeType.string, NodeType.number].includes(filterEquation[i].nodeType)) dataStack.push({ ...filterEquation[i] });
        else if (filterEquation[i].nodeType === NodeType.group)
          if (filterEquation[i].nodeValue === '(') {
            operatorStack.push({ ...filterEquation[i] });
            dataStack.push({ ...filterEquation[i] });
          } else {
            let operatorOnTop1 = operatorAt(-1, operatorStack);
            for (; operatorOnTop1 && operatorOnTop1.nodeValue !== '('; this.executeOperator(operatorStack.pop()!, dataStack))
              operatorOnTop1 = operatorAt(-2, operatorStack);
            operatorStack.pop();
            if (operatorOnTop1 && operatorOnTop1.nodeValue === '(') {
              const dataOnTop = dataStack.pop();
              dataStack.pop();
              dataStack.push(dataOnTop!);
            }
          }
        else {
          // Validate the UPPER and LOWER syntax (i.e.: must be followed by an opening parenthesis)
          if (
            ['upper', 'lower'].includes(filterEquation[i].nodeValue as string) &&
            (filterEquation.length === i + 1 ||
              (filterEquation[i + 1].nodeType !== NodeType.group && filterEquation[i + 1].nodeValue !== '('))
          )
            throw new Error(`Invalid vector layer filter (${(filterEquation[i].nodeValue as string).toUpperCase()} syntax error).`);

          for (
            let operatorOnTop2 = operatorAt(-1, operatorStack);
            operatorOnTop2 && operatorOnTop2.nodeValue !== '(' && findPriority(operatorOnTop2) > findPriority(filterEquation[i]);
            this.executeOperator(operatorStack.pop()!, dataStack)
          )
            operatorOnTop2 = operatorAt(-2, operatorStack);
          operatorStack.push({ ...filterEquation[i] });
        }
      }
      for (
        let operatorOnTop3 = operatorAt(-1, operatorStack);
        operatorOnTop3 && operatorOnTop3.nodeValue !== '(';
        this.executeOperator(operatorStack.pop()!, dataStack)
      )
        operatorOnTop3 = operatorAt(-2, operatorStack);
      operatorStack.pop();
    } catch (error: unknown) {
      throw new Error(`Invalid vector layer filter (${error}.`);
    }
    if (dataStack.length !== 1 || dataStack[0].nodeType !== NodeType.variable)
      throw new Error(`Invalid vector layer filter (invalid structure).`);
    const dataStackTop = dataStack.pop();
    return dataStackTop ? !(dataStackTop.nodeValue as boolean) : undefined;
  }

  // #region PROCESS RENDERER

  /**
   * Process a circle symbol using the settings.
   *
   * @param {TypeSimpleSymbolVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processCircleSymbol(settings: TypeSimpleSymbolVectorConfig): Style | undefined {
    // eslint-disable-next-line no-param-reassign
    if (settings.color === undefined) settings.color = this.getDefaultColor(0.25, true);
    const fillOptions: FillOptions = { color: settings.color };
    const strokeOptions: StrokeOptions = this.createStrokeOptions(settings);
    const circleOptions: CircleOptions = { radius: settings.size !== undefined ? settings.size : 4 };
    circleOptions.stroke = new Stroke(strokeOptions);
    circleOptions.fill = new Fill(fillOptions);
    if (settings.offset !== undefined) circleOptions.displacement = settings.offset;
    if (settings.rotation !== undefined) circleOptions.rotation = settings.rotation;
    return new Style({
      image: new StyleCircle(circleOptions),
    });
  }

  /**
   * Process a star shape symbol using the settings.
   *
   * @param {TypeSimpleSymbolVectorConfig} settings - Settings to use for the Style creation.
   * @param {number} points - Number of points needed to create the symbol.
   * @param {number} angle - Angle to use for the symbol creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processStarShapeSymbol(settings: TypeSimpleSymbolVectorConfig, points: number, angle: number): Style | undefined {
    // eslint-disable-next-line no-param-reassign
    if (settings.color === undefined) settings.color = this.getDefaultColor(0.25, true);
    const fillOptions: FillOptions = { color: settings.color };
    const strokeOptions: StrokeOptions = this.createStrokeOptions(settings);
    const regularShapeOptions: RegularShapeOptions = {
      radius: settings.size !== undefined ? settings.size : 6,
      radius2: settings.size !== undefined ? settings.size / 3 : 2,
      angle,
      points,
    };
    regularShapeOptions.stroke = new Stroke(strokeOptions);
    regularShapeOptions.fill = new Fill(fillOptions);
    if (settings.offset !== undefined) regularShapeOptions.displacement = settings.offset;
    if (settings.rotation !== undefined) regularShapeOptions.rotation = settings.rotation;
    return new Style({
      image: new RegularShape(regularShapeOptions),
    });
  }

  /**
   * Process a star symbol using the settings.
   *
   * @param {TypeSimpleSymbolVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processStarSymbol(settings: TypeSimpleSymbolVectorConfig): Style | undefined {
    return this.processStarShapeSymbol(settings, 5, 0);
  }

  /**
   * Process a X symbol using the settings.
   *
   * @param {TypeSimpleSymbolVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processXSymbol(settings: TypeSimpleSymbolVectorConfig): Style | undefined {
    return this.processStarShapeSymbol(settings, 4, Math.PI / 4);
  }

  /**
   * Process a + symbol using the settings.
   *
   * @param {TypeSimpleSymbolVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processPlusSymbol(settings: TypeSimpleSymbolVectorConfig): Style | undefined {
    return this.processStarShapeSymbol(settings, 4, 0);
  }

  /**
   * Process a regular shape using the settings, the number of points, the angle and the scale.
   *
   * @param {TypeSimpleSymbolVectorConfig} settings - Settings to use for the Style creation.
   * @param {number} points - Number of points needed to create the symbol.
   * @param {number} angle - Angle to use for the symbol creation.
   * @param {[number, number]} scale - Scale to use for the symbol creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processRegularShape(
    settings: TypeSimpleSymbolVectorConfig,
    points: number,
    angle: number,
    scale: [number, number]
  ): Style | undefined {
    // eslint-disable-next-line no-param-reassign
    if (settings.color === undefined) settings.color = this.getDefaultColor(0.25, true);
    const fillOptions: FillOptions = { color: settings.color };
    const strokeOptions: StrokeOptions = this.createStrokeOptions(settings);
    const regularShapeOptions: RegularShapeOptions = {
      radius: settings.size !== undefined ? settings.size : 6,
      angle,
      scale,
      points,
    };
    regularShapeOptions.stroke = new Stroke(strokeOptions);
    regularShapeOptions.fill = new Fill(fillOptions);
    if (settings.offset !== undefined) regularShapeOptions.displacement = settings.offset;
    if (settings.rotation !== undefined) regularShapeOptions.rotation = settings.rotation;
    return new Style({
      image: new RegularShape(regularShapeOptions),
    });
  }

  /**
   * Process a square symbol using the settings.
   *
   * @param {TypeSimpleSymbolVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processSquareSymbol(settings: TypeSimpleSymbolVectorConfig): Style | undefined {
    return this.processRegularShape(settings, 4, Math.PI / 4, [1, 1]);
  }

  /**
   * Process a Diamond symbol using the settings.
   *
   * @param {TypeSimpleSymbolVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processDiamondSymbol(settings: TypeSimpleSymbolVectorConfig): Style | undefined {
    return this.processRegularShape(settings, 4, 0, [0.75, 1]);
  }

  /**
   * Process a triangle symbol using the settings.
   *
   * @param {TypeSimpleSymbolVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processTriangleSymbol(settings: TypeSimpleSymbolVectorConfig): Style | undefined {
    return this.processRegularShape(settings, 3, 0, [1, 1]);
  }

  /**
   * Process an icon symbol using the settings.
   *
   * @param {TypeIconSymbolVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processIconSymbol(settings: TypeIconSymbolVectorConfig): Style | undefined {
    const iconOptions: IconOptions = {};
    iconOptions.src = `data:${settings.mimeType};base64,${settings.src}`;
    if (settings.width !== undefined && settings.height !== undefined) iconOptions.size = [settings.width, settings.height];
    if (settings.offset !== undefined) iconOptions.offset = settings.offset;
    if (settings.rotation !== undefined) iconOptions.rotation = settings.rotation;
    if (settings.opacity !== undefined) iconOptions.opacity = settings.opacity;
    if (settings.scale !== undefined) iconOptions.scale = settings.scale;
    return new Style({
      image: new StyleIcon(iconOptions),
    });
  }

  /**
   * Process a simple point symbol using the settings. Simple point symbol may be an icon or a vector symbol.
   *
   * @param {TypeLayerStyleSettings | TypeKindOfVectorSettings} styleSettings - Settings to use for the Style creation.
   * @param {Feature} feature - Optional feature. This method does not use it, it is there to have a homogeneous signature.
   * @param {FilterNodeType[]} filterEquation - Filter equation associated to the layer.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processSimplePoint(
    styleSettings: TypeLayerStyleSettings | TypeKindOfVectorSettings,
    feature?: Feature,
    filterEquation?: FilterNodeType[]
  ): Style | undefined {
    if (filterEquation !== undefined && filterEquation.length !== 0 && feature)
      if (this.featureIsNotVisible(feature, filterEquation)) return undefined;

    const settings = (styleSettings.type === 'simple' ? styleSettings.info[0].settings : styleSettings) as TypeKindOfVectorSettings;
    if (isSimpleSymbolVectorConfig(settings)) {
      const { symbol } = settings;
      return this.#processSymbol[symbol](settings);
    }
    if (isIconSymbolVectorConfig(settings)) return this.processIconSymbol(settings);
    return undefined;
  }

  /**
   * Process a simple lineString using the settings.
   *
   * @param {TypeStyleSettings | TypeKindOfVectorSettings} styleSettings - Settings to use for the Style creation.
   * @param {Feature} feature - Optional feature. This method does not use it, it is there to have a homogeneous signature.
   * @param {FilterNodeType[]} filterEquation - Filter equation associated to the layer.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processSimpleLineString(
    styleSettings: TypeLayerStyleSettings | TypeKindOfVectorSettings,
    feature?: Feature,
    filterEquation?: FilterNodeType[]
  ): Style | undefined {
    if (filterEquation !== undefined && filterEquation.length !== 0 && feature)
      if (this.featureIsNotVisible(feature, filterEquation)) return undefined;

    const settings = (styleSettings.type === 'simple' ? styleSettings.info[0].settings : styleSettings) as TypeKindOfVectorSettings;
    let geometry;
    if (feature) {
      geometry = feature.getGeometry() as Geometry;
    }
    if (isLineStringVectorConfig(settings)) {
      const strokeOptions: StrokeOptions = this.createStrokeOptions(settings);
      return new Style({ stroke: new Stroke(strokeOptions), geometry });
    }
    return undefined;
  }

  /**
   * Process a simple solid fill (polygon) using the settings.
   *
   * @param {TypePolygonVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processSolidFill(settings: TypePolygonVectorConfig, geometry?: Geometry): Style | undefined {
    // eslint-disable-next-line no-param-reassign
    if (settings.color === undefined) settings.color = this.getDefaultColor(0.25, true);
    const fillOptions: FillOptions = { color: settings.color };
    const strokeOptions: StrokeOptions = this.createStrokeOptions(settings);
    return new Style({
      stroke: new Stroke(strokeOptions),
      fill: new Fill(fillOptions),
      geometry,
    });
  }

  /**
   * Process a null fill (polygon with fill opacity = 0) using the settings.
   *
   * @param {TypePolygonVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processNullFill(settings: TypePolygonVectorConfig, geometry?: Geometry): Style | undefined {
    // eslint-disable-next-line no-param-reassign
    if (settings.color === undefined) settings.color = this.getDefaultColor(0, true);
    const fillOptions: FillOptions = { color: settings.color };
    const strokeOptions: StrokeOptions = this.createStrokeOptions(settings);
    return new Style({
      stroke: new Stroke(strokeOptions),
      fill: new Fill(fillOptions),
      geometry,
    });
  }

  /**
   * Process a pattern fill using the settings.
   *
   * @param {TypePolygonVectorConfig} settings - Settings to use for the Style creation.
   * @param {FillPatternLine[]} FillPatternLines - Fill pattern lines needed to create the fill.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processPatternFill(
    settings: TypePolygonVectorConfig,
    FillPatternLines: FillPatternLine[],
    geometry?: Geometry
  ): Style | undefined {
    // Depending on the pattern
    const patternSize = settings.patternSize ?? settings.paternSize ?? 8;
    const patternWidth = settings.patternWidth ?? 16;

    // eslint-disable-next-line no-param-reassign
    if (settings.color === undefined) settings.color = this.getDefaultColor(0.25, true);
    const fillOptions: FillOptions = { color: settings.color };
    const strokeOptions: StrokeOptions = this.createStrokeOptions(settings);

    // Canvas used to create the pattern fill. It is bigger to take into account the repeating aspect of the fill.
    const drawingCanvas = document.createElement('canvas');
    const context = drawingCanvas.getContext('2d', { willReadFrequently: true })!;

    // If fill style is a dot
    if (settings.fillStyle === 'dot') {
      const tileSpace = patternSize + patternWidth;
      drawingCanvas.width = tileSpace;
      drawingCanvas.height = tileSpace;

      // Draw the dot
      context.fillStyle = settings.color!;
      context.beginPath();
      context.arc(tileSpace / 2, tileSpace / 2, patternSize / 2, 0, 2 * Math.PI);
      context.fill();

      fillOptions.color = context.createPattern(drawingCanvas, 'repeat')!;
    } else {
      // Set the canvas width/height
      drawingCanvas.width = patternSize * 2;
      drawingCanvas.height = patternSize * 2;

      context.strokeStyle = settings.color;
      context.lineCap = 'round';
      context.lineWidth = settings.patternWidth ?? settings.paternWidth ?? 1;
      context.beginPath();
      for (let i = 0; i < FillPatternLines.length; i++) {
        const { moveTo, lineTo } = FillPatternLines[i];
        context.moveTo(moveTo[0] * patternSize, moveTo[1] * patternSize);
        context.lineTo(lineTo[0] * patternSize, lineTo[1] * patternSize);
      }
      context.stroke();

      // extract the sub area that will define the pattern that will repeat properly.
      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = patternSize;
      outputCanvas.height = patternSize;
      const outputContext = outputCanvas.getContext('2d', { willReadFrequently: true })!;
      outputContext.putImageData(context.getImageData(patternSize / 2, patternSize / 2, patternSize, patternSize), 0, 0);

      fillOptions.color = outputContext.createPattern(outputCanvas, 'repeat');
    }

    return new Style({
      stroke: new Stroke(strokeOptions),
      fill: new Fill(fillOptions),
      geometry,
    });
  }

  /**
   * Process a backward diagonal fill using the settings.
   *
   * @param {TypePolygonVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processBackwardDiagonalFill(settings: TypePolygonVectorConfig, geometry?: Geometry): Style | undefined {
    return this.processPatternFill(settings, this.FillPatternSettings.backwardDiagonal, geometry);
  }

  /**
   * Process a forward diagonal fill using the settings.
   *
   * @param {TypePolygonVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processForwardDiagonalFill(settings: TypePolygonVectorConfig, geometry?: Geometry): Style | undefined {
    return this.processPatternFill(settings, this.FillPatternSettings.forwardDiagonal, geometry);
  }

  /**
   * Process a cross fill using the settings.
   *
   * @param {TypePolygonVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processCrossFill(settings: TypePolygonVectorConfig, geometry?: Geometry): Style | undefined {
    return this.processPatternFill(settings, this.FillPatternSettings.cross, geometry);
  }

  /**
   * Process a diagonal cross fill using the settings.
   *
   * @param {TypePolygonVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processDiagonalCrossFill(settings: TypePolygonVectorConfig, geometry?: Geometry): Style | undefined {
    return this.processPatternFill(settings, this.FillPatternSettings.diagonalCross, geometry);
  }

  /**
   * Process a horizontal fill using the settings.
   *
   * @param {TypePolygonVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processHorizontalFill(settings: TypePolygonVectorConfig, geometry?: Geometry): Style | undefined {
    return this.processPatternFill(settings, this.FillPatternSettings.horizontal, geometry);
  }

  /**
   * Process a vertical fill using the settings.
   *
   * @param {TypePolygonVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processVerticalFill(settings: TypePolygonVectorConfig, geometry?: Geometry): Style | undefined {
    return this.processPatternFill(settings, this.FillPatternSettings.vertical, geometry);
  }

  /**
   * Process a dot fill using the settings.
   *
   * @param {TypePolygonVectorConfig} settings - Settings to use for the Style creation.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processDotFill(settings: TypePolygonVectorConfig, geometry?: Geometry): Style | undefined {
    return this.processPatternFill(settings, this.FillPatternSettings.dot, geometry);
  }

  /**
   * Process a simple polygon using the settings.
   *
   * @param {TypeStyleSettings | TypeKindOfVectorSettings} styleSettings - Settings to use for the Style creation.
   * @param {Feature} feature - Optional feature. This method does not use it, it is there to have a homogeneous signature.
   * @param {FilterNodeType[]} filterEquation - Filter equation associated to the layer.
   *
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processSimplePolygon(
    styleSettings: TypeLayerStyleSettings | TypeKindOfVectorSettings,
    feature?: Feature,
    filterEquation?: FilterNodeType[]
  ): Style | undefined {
    if (filterEquation !== undefined && filterEquation.length !== 0 && feature)
      if (this.featureIsNotVisible(feature, filterEquation)) return undefined;

    const settings = (styleSettings.type === 'simple' ? styleSettings.info[0].settings : styleSettings) as TypeKindOfVectorSettings;
    let geometry;
    if (feature) {
      geometry = feature.getGeometry() as Geometry;
    }
    if (isFilledPolygonVectorConfig(settings)) {
      const { fillStyle } = settings; // TODO: refactor - introduce by moving to config map schema type
      if (geometry !== undefined) {
        return this.#processFillStyle[fillStyle](settings, geometry);
      }
      return this.#processFillStyle[fillStyle](settings);
    }
    return undefined;
  }

  // #endregion PROCESS RENDERER

  /**
   * This method is used to process the array of point styles as described in the pointStyleConfig.
   *
   * @param {TypeVectorLayerStyles} layerStyle - Object that will receive the created canvas.
   * @param {TypeLayerStyleConfigInfo[]} arrayOfPointStyleConfig - Array of point style configuration.
   * @returns {Promise<TypeVectorLayerStyles>} A promise that the vector layer style is created.
   */
  static async processArrayOfPointStyleConfig(
    layerStyles: TypeVectorLayerStyles,
    arrayOfPointStyleConfig: TypeLayerStyleConfigInfo[]
  ): Promise<TypeVectorLayerStyles> {
    try {
      // UniqueValue or ClassBreak point style configuration ============================================================
      const styleArray: (HTMLCanvasElement | null)[] = layerStyles.Point!.arrayOfCanvas!;
      const promiseOfCanvasCreated: Promise<HTMLCanvasElement | null>[] = [];
      for (let i = 0; i < arrayOfPointStyleConfig.length; i++) {
        if (isIconSymbolVectorConfig(arrayOfPointStyleConfig[i].settings))
          // Icon symbol ================================================================================================
          promiseOfCanvasCreated.push(this.createIconCanvas(this.processSimplePoint(arrayOfPointStyleConfig[i].settings)));
        // Simple vector symbol =======================================================================================
        else
          promiseOfCanvasCreated.push(
            new Promise<HTMLCanvasElement | null>((resolveSimpleVectorSymbol) => {
              resolveSimpleVectorSymbol(this.createPointCanvas(this.processSimplePoint(arrayOfPointStyleConfig[i].settings)));
            })
          );
      }
      const listOfCanvasCreated = await Promise.all(promiseOfCanvasCreated);
      listOfCanvasCreated.forEach((canvas) => {
        styleArray.push(canvas);
      });
      return layerStyles;
    } catch (error: unknown) {
      logger.logError('Error processing array of point styles', error);
      return {} as TypeVectorLayerStyles;
    }
  }

  /**
   * This method is a private sub routine used by the getLegendStyles method to gets the style of the layer as specified by the
   * style configuration.
   *
   * @param {TypeKindOfVectorSettings | undefined} defaultSettings - Settings associated to simple styles or default style of
   * unique value and class break styles. When this parameter is undefined, no defaultCanvas is created.
   * @param {TypeLayerStyleConfigInfo[] | undefined} arrayOfPointStyleConfig - Array of point style
   * configuration associated to unique value and class break styles. When this parameter is undefined, no arrayOfCanvas is
   * created.
   *
   * @returns {Promise<TypeVectorLayerStyles>} A promise that the layer styles are processed.
   */
  static async getPointStyleSubRoutine(
    defaultSettings?: TypeKindOfVectorSettings,
    arrayOfPointStyleConfig?: TypeLayerStyleConfigInfo[]
  ): Promise<TypeVectorLayerStyles> {
    try {
      const layerStyles: TypeVectorLayerStyles = { Point: {} };
      if (defaultSettings) {
        if (isIconSymbolVectorConfig(defaultSettings)) {
          // Icon symbol ======================================================================================
          const canvas = await this.createIconCanvas(this.processSimplePoint(defaultSettings));
          layerStyles.Point!.defaultCanvas = canvas;
          if (arrayOfPointStyleConfig) {
            layerStyles.Point!.arrayOfCanvas = [];
            const processedStyles = await this.processArrayOfPointStyleConfig(layerStyles, arrayOfPointStyleConfig);
            processedStyles.Point?.arrayOfCanvas?.pop();
            return processedStyles;
          }
          return layerStyles;
        }

        // Simple vector symbol =============================================================================
        layerStyles.Point!.defaultCanvas = this.createPointCanvas(this.processSimplePoint(defaultSettings));
        if (arrayOfPointStyleConfig) {
          layerStyles.Point!.arrayOfCanvas = [];
          const processedStyles = await this.processArrayOfPointStyleConfig(layerStyles, arrayOfPointStyleConfig);
          processedStyles.Point?.arrayOfCanvas?.pop();
          return processedStyles;
        }
        return layerStyles;
      }

      layerStyles.Point!.arrayOfCanvas = [];
      return await this.processArrayOfPointStyleConfig(layerStyles, arrayOfPointStyleConfig!);
    } catch (error: unknown) {
      logger.logError('Error getPointStyle sub routine', error);
      return {} as TypeVectorLayerStyles;
    }
  }

  /**
   * This method gets the legend styles used by the the layer as specified by the style configuration.
   *
   * @param {TypeStyleConfig} styleConfig - The style configuration.
   *
   * @returns {Promise<TypeVectorLayerStyles>} A promise that the layer styles are processed.
   */
  static async getLegendStyles(styleConfig: TypeLayerStyleConfig | undefined): Promise<TypeVectorLayerStyles> {
    try {
      if (!styleConfig) return {};
      const legendStyles: TypeVectorLayerStyles = {};

      if (styleConfig.Point) {
        // ======================================================================================================================
        // Point style configuration ============================================================================================
        if (styleConfig.Point.type === 'simple') {
          const layerStyles = await this.getPointStyleSubRoutine(styleConfig.Point.info[0].settings);
          legendStyles.Point = layerStyles.Point;
        } else {
          const defaultSettings = styleConfig.Point.hasDefault
            ? styleConfig.Point.info[styleConfig.Point.info.length - 1].settings
            : undefined;
          const layerStyles = await this.getPointStyleSubRoutine(defaultSettings, styleConfig.Point.info);
          legendStyles.Point = layerStyles.Point;
        }
      }

      if (styleConfig.LineString) {
        // ======================================================================================================================
        // LineString style configuration =======================================================================================
        const layerStyles: TypeVectorLayerStyles = { LineString: {} };
        if (styleConfig.LineString.type === 'simple') {
          layerStyles.LineString!.defaultCanvas = this.createLineStringCanvas(this.processSimpleLineString(styleConfig.LineString));
        } else {
          if (styleConfig.LineString.hasDefault)
            layerStyles.LineString!.defaultCanvas = this.createLineStringCanvas(
              this.processSimpleLineString(styleConfig.LineString.info[styleConfig.LineString.info.length - 1].settings)
            );
          const styleArray: HTMLCanvasElement[] = [];
          styleConfig.LineString.info.forEach((styleInfo) => {
            styleArray.push(this.createLineStringCanvas(this.processSimpleLineString(styleInfo.settings)));
          });
          if (styleConfig.LineString.hasDefault) styleArray.pop();
          layerStyles.LineString!.arrayOfCanvas = styleArray;
        }
        legendStyles.LineString = layerStyles.LineString;
      }

      if (styleConfig.Polygon) {
        // ======================================================================================================================
        // Polygon style configuration ==========================================================================================
        const layerStyles: TypeVectorLayerStyles = { Polygon: {} };
        if (styleConfig.Polygon.type === 'simple') {
          layerStyles.Polygon!.defaultCanvas = this.createPolygonCanvas(this.processSimplePolygon(styleConfig.Polygon.info[0].settings));
        } else {
          if (styleConfig.Polygon.hasDefault)
            layerStyles.Polygon!.defaultCanvas = this.createPolygonCanvas(
              this.processSimplePolygon(styleConfig.Polygon.info[styleConfig.Polygon.info.length - 1].settings)
            );

          const styleArray: HTMLCanvasElement[] = [];
          styleConfig.Polygon.info.forEach((styleInfo) => {
            styleArray.push(this.createPolygonCanvas(this.processSimplePolygon(styleInfo.settings)));
          });
          if (styleConfig.Polygon.hasDefault) styleArray.pop();
          layerStyles.Polygon!.arrayOfCanvas = styleArray;
        }

        legendStyles.Polygon = layerStyles.Polygon;
      }
      return legendStyles;
    } catch (error: unknown) {
      logger.logError('Error getLegendStyles', error);
      return {};
    }
  }

  /**
   * Create a default style to use with a vector feature that has no style configuration.
   *
   * @param {TypeStyleGeometry} geometryType - Type of geometry (Point, LineString, Polygon).
   * @param {string} label - Label for the style.
   *
   * @returns {TypeLayerStyleConfigInfo | undefined} The Style configuration created. Undefined if unable to create it.
   */
  static createDefaultStyle(geometryType: TypeStyleGeometry, label: string): TypeLayerStyleSettings | undefined {
    if (geometryType === 'Point') {
      const settings: TypeSimpleSymbolVectorConfig = {
        type: 'simpleSymbol',
        color: this.getDefaultColor(0.25),
        stroke: {
          color: this.getDefaultColor(1, true),
          lineStyle: 'solid',
          width: 1,
        },
        symbol: 'circle',
      };
      return { type: 'simple', hasDefault: false, fields: [], info: [{ visible: true, label, settings, values: [] }] };
    }
    if (geometryType === 'LineString') {
      const settings: TypeLineStringVectorConfig = {
        type: 'lineString',
        stroke: { color: this.getDefaultColor(1, true) },
      };
      return { type: 'simple', hasDefault: false, fields: [], info: [{ visible: true, label, settings, values: [] }] };
    }
    if (geometryType === 'Polygon') {
      const settings: TypePolygonVectorConfig = {
        type: 'filledPolygon',
        color: this.getDefaultColor(0.25),
        stroke: { color: this.getDefaultColor(1, true) },
        fillStyle: 'solid',
      };
      return { type: 'simple', hasDefault: false, fields: [], info: [{ visible: true, label, settings, values: [] }] };
    }
    logger.logError(`Geometry type ${geometryType} is not supported by the GeoView viewer.`);
    return undefined;
  }

  /**
   * Search the unique value entry using the field values stored in the feature.
   *
   * @param {string[]} fields - Fields involved in the unique value definition.
   * @param {TypeLayerStyleConfigInfo[]?} uniqueValueStyleInfo - Unique value configuration.
   * @param {Feature?} feature - Feature used to test the unique value conditions.
   * @param {TypeLayerMetadataFields[]?} domainsLookup - An optional lookup table to handle coded value domains.
   * @param {TypeAliasLookup?} aliasLookup - An optional lookup table to handle field name aliases.
   * @returns {TypeLayerStyleConfigInfo | undefined} The Style created. Undefined if unable to create it.
   */
  static searchUniqueValueEntry(
    fields: string[],
    uniqueValueStyleInfo: TypeLayerStyleConfigInfo[],
    feature?: Feature,
    domainsLookup?: TypeLayerMetadataFields[],
    aliasLookup?: TypeAliasLookup
  ): TypeLayerStyleConfigInfo | undefined {
    // If no feature
    if (!feature) return undefined;

    // Get the feature keys
    const featureKeys = feature.getKeys();

    // Start looping on the unique value style to find
    for (let i = 0; i < uniqueValueStyleInfo.length; i++) {
      // Start looping on the fields
      let allFieldsMatched = true;
      for (let j = 0; j < fields.length; j++) {
        const field = fields[j];
        const expectedValue = uniqueValueStyleInfo[i].values[j];

        // Get the target field name: check case-insensitive match in feature keys
        let fieldName = featureKeys.find((key) => key.toLowerCase() === field.toLowerCase());

        // Try alias if no match found, then re-validate the alias key against feature keys
        if (!fieldName && aliasLookup?.[field]) {
          const alias = aliasLookup[field];
          fieldName = featureKeys.find((key) => key.toLowerCase() === alias.toLowerCase());
        }

        if (!fieldName) {
          logger.logWarning(`Renderer searchUniqueValueEntry: Cannot find field "${field}"`);
          return undefined;
        }

        // Read the actual value
        let actualValue = feature.get(fieldName);

        // First try direct match
        // eslint-disable-next-line eqeqeq
        let matched = actualValue == expectedValue;

        // If not matched, check coded domain
        if (!matched) {
          const fieldDomain = domainsLookup?.find((domain) => domain.name === fieldName)?.domain;
          // Cast as codedvaluetype
          const fieldDomainCasted = fieldDomain as codedValueType;
          if (fieldDomainCasted?.codedValues) {
            const codedValue = fieldDomainCasted.codedValues.find((dom) => dom.name === actualValue);
            if (codedValue) {
              actualValue = codedValue.code;
              // eslint-disable-next-line eqeqeq
              matched = actualValue == expectedValue;
            }
          }
        }

        // If this field did not match, mark the whole thing as a mismatch
        if (!matched) {
          allFieldsMatched = false;
          break;
        }
      }

      if (allFieldsMatched) {
        return uniqueValueStyleInfo[i];
      }
    }

    return undefined;
  }

  /**
   * Process the unique value settings using a point feature to get its Style.
   *
   * @param {TypeStyleSettings | TypeKindOfVectorSettings} styleSettings - Style settings to use.
   * @param {Feature?} feature - Feature used to test the unique value conditions.
   * @param {FilterNodeType[]?} filterEquation - Filter equation associated to the layer.
   * @param {boolean?} legendFilterIsOff - When true, do not apply legend filter.
   * @param {TypeLayerMetadataFields[]?} domainsLookup - An optional lookup table to handle coded value domains.
   * @param {TypeAliasLookup?} aliasLookup - An optional lookup table to handle field name aliases.
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processUniqueValuePoint(
    styleSettings: TypeLayerStyleSettings | TypeKindOfVectorSettings,
    feature?: Feature,
    filterEquation?: FilterNodeType[],
    legendFilterIsOff?: boolean,
    domainsLookup?: TypeLayerMetadataFields[],
    aliasLookup?: TypeAliasLookup
  ): Style | undefined {
    if (filterEquation !== undefined && filterEquation.length !== 0 && feature)
      if (this.featureIsNotVisible(feature, filterEquation)) return undefined;

    if (styleSettings.type === 'uniqueValue') {
      const { hasDefault, fields, info } = styleSettings;
      const styleEntry = this.searchUniqueValueEntry(fields, info, feature, domainsLookup, aliasLookup);
      if (styleEntry !== undefined && (legendFilterIsOff || styleEntry.visible !== false))
        return this.processSimplePoint(styleEntry.settings);

      // TODO: Check - Why look for styleSettings.info.length - 1?
      if (
        styleEntry === undefined &&
        hasDefault &&
        (legendFilterIsOff || styleSettings.info[styleSettings.info.length - 1].visible !== false)
      )
        return this.processSimplePoint(styleSettings.info[styleSettings.info.length - 1].settings);
    }
    return undefined;
  }

  /**
   * Process the unique value settings using a lineString feature to get its Style.
   *
   * @param {TypeLayerStyleSettings | TypeKindOfVectorSettings} styleSettings - Style settings to use.
   * @param {Feature?} feature - Feature used to test the unique value conditions.
   * @param {FilterNodeType[]?} filterEquation - Filter equation associated to the layer.
   * @param {boolean?} legendFilterIsOff - When true, do not apply legend filter.
   * @param {TypeLayerMetadataFields[]?} domainsLookup - An optional lookup table to handle coded value domains.
   * @param {TypeAliasLookup?} aliasLookup - An optional lookup table to handle field name aliases.
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processUniqueLineString(
    styleSettings: TypeLayerStyleSettings | TypeKindOfVectorSettings,
    feature?: Feature,
    filterEquation?: FilterNodeType[],
    legendFilterIsOff?: boolean,
    domainsLookup?: TypeLayerMetadataFields[],
    aliasLookup?: TypeAliasLookup
  ): Style | undefined {
    if (filterEquation !== undefined && filterEquation.length !== 0 && feature)
      if (this.featureIsNotVisible(feature, filterEquation)) return undefined;

    if (styleSettings.type === 'uniqueValue') {
      const { hasDefault, fields, info } = styleSettings;
      const styleEntry = this.searchUniqueValueEntry(fields, info, feature, domainsLookup, aliasLookup);
      if (styleEntry !== undefined && (legendFilterIsOff || styleEntry.visible !== false))
        return this.processSimpleLineString(styleEntry.settings, feature);

      // TODO: Check - Why look for info.length - 1?
      if (styleEntry === undefined && hasDefault && (legendFilterIsOff || info[info.length - 1].visible !== false))
        return this.processSimpleLineString(info[info.length - 1].settings, feature);
    }
    return undefined;
  }

  /**
   * Process the unique value settings using a polygon feature to get its Style.
   *
   * @param {TypeLayerStyleSettings | TypeKindOfVectorSettings} styleSettings - Style settings to use.
   * @param {Feature?} feature - Feature used to test the unique value conditions.
   * @param {FilterNodeType[]?} filterEquation - Filter equation associated to the layer.
   * @param {boolean} legendFilterIsOff - When true, do not apply legend filter.
   * @param {TypeLayerMetadataFields[]?} domainsLookup - An optional lookup table to handle coded value domains.
   * @param {TypeAliasLookup?} aliasLookup - An optional lookup table to handle field name aliases.
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processUniquePolygon(
    styleSettings: TypeLayerStyleSettings | TypeKindOfVectorSettings,
    feature?: Feature,
    filterEquation?: FilterNodeType[],
    legendFilterIsOff?: boolean,
    domainsLookup?: TypeLayerMetadataFields[],
    aliasLookup?: TypeAliasLookup
  ): Style | undefined {
    if (filterEquation !== undefined && filterEquation.length !== 0 && feature)
      if (this.featureIsNotVisible(feature, filterEquation)) return undefined;

    if (styleSettings.type === 'uniqueValue') {
      const { hasDefault, fields, info } = styleSettings;
      const styleEntry = this.searchUniqueValueEntry(fields, info, feature, domainsLookup, aliasLookup);
      if (styleEntry !== undefined && (legendFilterIsOff || styleEntry.visible !== false))
        return this.processSimplePolygon(styleEntry.settings, feature);

      // TODO: Check - Why look for info.length - 1?
      if (styleEntry === undefined && hasDefault && (legendFilterIsOff || info[info.length - 1].visible !== false))
        return this.processSimplePolygon(info[info.length - 1].settings, feature);
    }
    return undefined;
  }

  /**
   * Search the class breakentry using the field value stored in the feature.
   *
   * @param {string} field - Field involved in the class break definition.
   * @param {TypeLayerStyleConfigInfo[]} classBreakStyleInfo - Class break configuration.
   * @param {Feature} feature - Feature used to test the class break conditions.
   * @param {TypeAliasLookup?} aliasLookup - An optional lookup table to handle field name aliases.
   * @returns {number | undefined} The index of the entry. Undefined if unable to find it.
   */
  static searchClassBreakEntry(
    field: string,
    classBreakStyleInfo: TypeLayerStyleConfigInfo[],
    feature: Feature,
    aliasLookup?: TypeAliasLookup
  ): TypeLayerStyleConfigInfo | undefined {
    // For obscure reasons, it seems that sometimes the field names in the feature do not have the same case as those in the
    // class break definition.
    const featureKey = feature.getKeys().filter((key) => {
      return key.toLowerCase() === field.toLowerCase();
    });

    // Failed to find match: Attempt to find the alias key from the names
    if (featureKey.length !== 1 && aliasLookup && Object.keys(aliasLookup).length > 0) {
      if (field in Object.keys(aliasLookup)) {
        featureKey.push(aliasLookup[field]);
      }
    }

    if (featureKey.length !== 1) return undefined;

    const fieldValueRaw = feature.get(featureKey[0]);

    // Try to read it as a number, because we're in class break rendering mode, don't trust the service to sometimes return a string value like '1234' here..
    const fieldValue = Number(fieldValueRaw);

    // For each bucket
    for (let i = 0; i < classBreakStyleInfo.length; i++) {
      const minBreak = Number(classBreakStyleInfo[i].values[0]);
      const maxBreak = Number(classBreakStyleInfo[i].values[1]);
      let conditions = classBreakStyleInfo[i].valuesConditions;

      // If no conditions simulate an Esri behavior supporting legacy mode - i.e: the first bucket is min <= value <= max and the other buckets are min < value <= max (note the <= vs <)
      if (!conditions) {
        // If this is the first break, use ['gte', 'lte']
        if (i === 0) conditions = ['>=', '<='];
        // If this is the other breaks, use ['gt', 'lte']
        if (!conditions && i > 0) conditions = ['>', '<='];
      } // Now, conditions is set

      // Check the value vs the bounds and the conditions
      if (this.searchClassBreakEntryCheck(fieldValue, minBreak, maxBreak, conditions!)) {
        return classBreakStyleInfo[i];
      }
      // Continue searching the break class index
    }

    // Not found
    return undefined;
  }

  /**
   * Check whether a numeric value falls within a class-break interval using provided boundary conditions.
   * The `conditions` parameter is expected to be a two-element array where:
   * - conditions[0] is the lower-bound operator: 'gt' (>) or 'gte' (>=)
   * - conditions[1] is the upper-bound operator: 'lt' (<) or 'lte' (<=)
   * Examples:
   * - ['gte','lte'] => min <= value <= max
   * - ['gt','lte']  => min < value <= max
   * @param {number} value - The numeric value to test.
   * @param {number} min - The lower bound of the interval.
   * @param {number} max - The upper bound of the interval.
   * @param {TypeLayerStyleValueCondition[]} conditions - Two-element array describing the boundary operators.
   * @returns {boolean} True if the value satisfies the interval according to the conditions, false otherwise.
   * @throws {NotSupportedError} If `conditions` contains an unsupported combination of operators.
   */
  static searchClassBreakEntryCheck(value: number, min: number, max: number, conditions: TypeLayerStyleValueCondition[]): boolean {
    // Depending on the conditions for minimum and maximum
    if (conditions[0] === '>' && conditions[1] === '<') {
      return min < value && value < max;
    } else if (conditions[0] === '>' && conditions[1] === '<=') {
      return min < value && value <= max;
    } else if (conditions[0] === '>=' && conditions[1] === '<') {
      return min <= value && value < max;
    } else if (conditions[0] === '>=' && conditions[1] === '<=') {
      return min <= value && value <= max;
    }

    // Unsupported
    throw new NotSupportedError('Unsupported conditions to compare the values for the class break renderer');
  }

  /**
   * Process the class break settings using a Point feature to get its Style.
   *
   * @param {TypeLayerStyleSettings | TypeKindOfVectorSettings} styleSettings - Style settings to use.
   * @param {Feature} feature - Feature used to test the unique value conditions.
   * @param {FilterNodeType[]} filterEquation - Filter equation associated to the layer.
   * @param {boolean} legendFilterIsOff - When true, do not apply legend filter.
   * @param {TypeLayerMetadataFields[]?} domainsLookup - An optional lookup table to handle coded value domains.
   * @param {TypeAliasLookup?} aliasLookup - An optional lookup table to handle field name aliases.
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processClassBreaksPoint(
    styleSettings: TypeLayerStyleSettings | TypeKindOfVectorSettings,
    feature?: Feature,
    filterEquation?: FilterNodeType[],
    legendFilterIsOff?: boolean,
    domainsLookup?: TypeLayerMetadataFields[],
    aliasLookup?: TypeAliasLookup
  ): Style | undefined {
    if (filterEquation !== undefined && filterEquation.length !== 0 && feature) {
      if (this.featureIsNotVisible(feature, filterEquation)) return undefined;
    }

    if (styleSettings.type === 'classBreaks') {
      const { hasDefault, fields, info } = styleSettings;
      const foundClassBreakInfo = this.searchClassBreakEntry(fields[0], info, feature!, aliasLookup);

      // If found a class break renderer that works for the value of the feature
      if (foundClassBreakInfo && (legendFilterIsOff || foundClassBreakInfo.visible !== false)) {
        return this.processSimplePoint(foundClassBreakInfo.settings);
      }

      // If not found a class break renderer that works, but we want default values
      // TODO: Check - I'm not sure we should be using info[info.length - 1] to know if the default should be visible?
      if (!foundClassBreakInfo && hasDefault && (legendFilterIsOff || info[info.length - 1].visible !== false)) {
        return this.processSimplePoint(info[info.length - 1].settings);
      }
    }
    return undefined;
  }

  /**
   * Process the class break settings using a lineString feature to get its Style.
   *
   * @param {TypeStyleSettings | TypeKindOfVectorSettings} styleSettings - Style settings to use.
   * @param {Feature} feature - Feature used to test the unique value conditions.
   * @param {FilterNodeType[]} filterEquation - Filter equation associated to the layer.
   * @param {boolean} legendFilterIsOff - When true, do not apply legend filter.
   * @param {TypeLayerMetadataFields[]?} domainsLookup - An optional lookup table to handle coded value domains.
   * @param {TypeAliasLookup?} aliasLookup - An optional lookup table to handle field name aliases.
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processClassBreaksLineString(
    styleSettings: TypeLayerStyleSettings | TypeKindOfVectorSettings,
    feature?: Feature,
    filterEquation?: FilterNodeType[],
    legendFilterIsOff?: boolean,
    domainsLookup?: TypeLayerMetadataFields[],
    aliasLookup?: TypeAliasLookup
  ): Style | undefined {
    if (filterEquation !== undefined && filterEquation.length !== 0 && feature) {
      if (this.featureIsNotVisible(feature, filterEquation)) return undefined;
    }

    if (styleSettings.type === 'classBreaks') {
      const { hasDefault, fields, info } = styleSettings;
      const foundClassBreakInfo = this.searchClassBreakEntry(fields[0], info, feature!, aliasLookup);

      // If found a class break renderer that works for the value of the feature
      if (foundClassBreakInfo && (legendFilterIsOff || foundClassBreakInfo.visible !== false)) {
        return this.processSimpleLineString(foundClassBreakInfo.settings, feature);
      }

      // If not found a class break renderer that works, but we want default values
      // TODO: Check - I'm not sure we should be using info[info.length - 1] to know if the default should be visible?
      if (!foundClassBreakInfo && hasDefault && (legendFilterIsOff || info[info.length - 1].visible !== false)) {
        return this.processSimpleLineString(info[info.length - 1].settings, feature);
      }
    }
    return undefined;
  }

  /**
   * Process the class break settings using a Polygon feature to get its Style.
   *
   * @param {TypeLayerStyleSettings | TypeKindOfVectorSettings} styleSettings - Style settings to use.
   * @param {Feature} feature - Feature used to test the unique value conditions.
   * @param {FilterNodeType[]} filterEquation - Filter equation associated to the layer.
   * @param {boolean} legendFilterIsOff - When true, do not apply legend filter.
   * @param {TypeLayerMetadataFields[]?} domainsLookup - An optional lookup table to handle coded value domains.
   * @param {TypeAliasLookup?} aliasLookup - An optional lookup table to handle field name aliases.
   * @returns {Style | undefined} The Style created. Undefined if unable to create it.
   */
  static processClassBreaksPolygon(
    styleSettings: TypeLayerStyleSettings | TypeKindOfVectorSettings,
    feature?: Feature,
    filterEquation?: FilterNodeType[],
    legendFilterIsOff?: boolean,
    domainsLookup?: TypeLayerMetadataFields[],
    aliasLookup?: TypeAliasLookup
  ): Style | undefined {
    if (filterEquation !== undefined && filterEquation.length !== 0 && feature) {
      if (this.featureIsNotVisible(feature, filterEquation)) return undefined;
    }

    if (styleSettings.type === 'classBreaks') {
      const { hasDefault, fields, info } = styleSettings;
      const foundClassBreakInfo = this.searchClassBreakEntry(fields[0], info, feature!, aliasLookup);

      // If found a class break renderer that works for the value of the feature
      if (foundClassBreakInfo && (legendFilterIsOff || foundClassBreakInfo.visible !== false)) {
        return this.processSimplePolygon(foundClassBreakInfo.settings, feature);
      }

      // If not found a class break renderer that works, but we want default values
      // TODO: Check - I'm not sure we should be using info[info.length - 1] to know if the default should be visible?
      if (!foundClassBreakInfo && hasDefault && (legendFilterIsOff || info[info.length - 1].visible !== false)) {
        return this.processSimplePolygon(info[info.length - 1].settings, feature);
      }
    }
    return undefined;
  }

  /**
   * This method gets the style of the feature using the layer entry config. If the style does not exist for the geometryType,
   * create it using the default style strategy.
   * @param {FeatureLike} feature - Feature that need its style to be defined.
   * @param {TypeStyleConfig} style - The style to use
   * @param {string} label - The style label when one has to be created
   * @param {FilterNodeType[]} filterEquation - Filter equation associated to the layer.
   * @param {boolean} legendFilterIsOff - When true, do not apply legend filter.
   * @param {TypeAliasLookup?} aliasLookup - An optional lookup table to handle field name aliases.
   * @param {() => Promise<string | null>} callbackWhenCreatingStyle - An optional callback to execute when a new style had to be created
   * @returns {Style | undefined} The style applied to the feature or undefined if not found.
   */
  static getAndCreateFeatureStyle(
    feature: FeatureLike,
    style: TypeLayerStyleConfig,
    label: string,
    filterEquation?: FilterNodeType[],
    legendFilterIsOff?: boolean,
    aliasLookup?: TypeAliasLookup,
    callbackWhenCreatingStyle?: (geometryType: TypeStyleGeometry, style: TypeLayerStyleConfigInfo) => void
  ): Style | undefined {
    // Get the geometry type
    const geometryType = this.getGeometryType(feature);

    // The style to work on
    let styleWorkOn = style;

    // If style does not exist for the geometryType, create it.
    if (!style || !style[geometryType]) {
      // Create a style on-the-fly for the geometry type, because the layer config didn't have one already
      const styleConfig = this.createDefaultStyle(geometryType, label);

      // If a style has been created on-the-fly
      if (styleConfig) {
        if (style) styleWorkOn[geometryType] = styleConfig;
        else styleWorkOn = { [geometryType]: styleConfig };
        callbackWhenCreatingStyle?.(geometryType, styleConfig.info[0]);
      }
    }

    // Get the style according to its type and geometry.
    if (styleWorkOn[geometryType]) {
      const styleSettings = style[geometryType]!;
      const { type } = styleSettings;
      // TODO: Refactor - Rewrite this to use explicit function calls instead, for clarity and references finding
      const featureStyle = this.processStyle[type][geometryType](
        styleSettings,
        feature as Feature,
        filterEquation,
        legendFilterIsOff,
        undefined,
        aliasLookup
      );

      return featureStyle;
    }

    return undefined;
  }

  /**
   * This method gets the image source from the style of the feature using the layer entry config.
   * @param {Feature} feature - The feature that need its icon to be defined.
   * @param {TypeStyleConfig} style - The style to use
   * @param {FilterNodeType[]?} filterEquation - Filter equation associated to the layer.
   * @param {boolean?} legendFilterIsOff - When true, do not apply legend filter.
   * @param {TypeLayerMetadataFields[]?} domainsLookup - An optional lookup table to handle coded value domains.
   * @param {TypeAliasLookup?} aliasLookup - An optional lookup table to handle field name aliases.
   * @returns {string} The icon associated to the feature or a default empty one.
   */
  static getFeatureImageSource(
    feature: Feature,
    style: TypeLayerStyleConfig,
    filterEquation?: FilterNodeType[],
    legendFilterIsOff?: boolean,
    domainsLookup?: TypeLayerMetadataFields[],
    aliasLookup?: TypeAliasLookup
  ): string | undefined {
    // The image source that will be returned (if calculated successfully)
    let imageSource: string | undefined;

    // GV: Sometimes, the feature will have no geometry e.g. esriDynamic as we fetch geometry only when needed
    // GV: We need to extract geometry from style instead. For esriDynamic there is only one geometry at a time
    // If the feature has a geometry or Style has a geometry
    if (feature.getGeometry() || Object.keys(style)[0]) {
      const geometryType = feature.getGeometry() ? this.getGeometryType(feature) : (Object.keys(style)[0] as TypeStyleGeometry);

      // Get the style accordingly to its type and geometry.
      if (style[geometryType]) {
        const styleSettings = style[geometryType];
        const { type } = styleSettings;

        // TODO: Performance #2688 - Wrap the style processing in a Promise to prevent blocking, Use requestAnimationFrame to process style during next frame
        // Wrap the style processing in a Promise to prevent blocking
        // return new Promise((resolve) => {
        //   // Use requestAnimationFrame to process style during next frame
        //   requestAnimationFrame(() => {
        //     const processedStyle = processStyle[type][geometryType](
        //       styleSettings,
        //       feature as Feature,
        //       filterEquation,
        //       legendFilterIsOff
        //     );
        //     resolve(processedStyle);
        //   });
        // });

        const featureStyle = this.processStyle[type][geometryType](
          styleSettings,
          feature,
          filterEquation,
          legendFilterIsOff,
          domainsLookup,
          aliasLookup
        );

        if (featureStyle) {
          if (geometryType === 'Point') {
            if (
              (styleSettings.type === 'simple' && !(featureStyle.getImage() instanceof Icon)) ||
              (styleSettings.type === 'uniqueValue' && !(featureStyle.getImage() instanceof Icon)) ||
              (styleSettings.type === 'classBreaks' && !(featureStyle.getImage() instanceof Icon))
            ) {
              imageSource = this.createPointCanvas(featureStyle).toDataURL();
            } else {
              imageSource = (featureStyle.getImage() as Icon).getSrc() || undefined;
            }
          } else if (geometryType === 'LineString') {
            imageSource = this.createLineStringCanvas(featureStyle).toDataURL();
          } else {
            imageSource = this.createPolygonCanvas(featureStyle).toDataURL();
          }
        }
      }
    }

    // If set, all good
    if (imageSource) return imageSource;

    return undefined;
  }

  /**
   * Classify the remaining nodes to complete the classification. The plus and minus can be a unary or a binary operator. It is
   * only at the end that we can determine there node type. Nodes that start with a number are numbers, otherwise they are
   * variables. If a problem is detected, an error object is thrown with an explanatory message.
   *
   * @param {FilterNodeType[]} keywordArray - Array of keywords to process.
   *
   * @returns {FilterNodeType[]} The new keywords array with all nodes classified.
   */
  static classifyUnprocessedNodes(keywordArray: FilterNodeType[]): FilterNodeType[] {
    return keywordArray.map((node, i) => {
      if (node.nodeType === NodeType.unprocessedNode) {
        if (Number.isNaN(Number((node.nodeValue as string).slice(0, 1)))) {
          if (['+', '-'].includes(node.nodeValue as string))
            if (i !== 0 && [NodeType.number, NodeType.string, NodeType.variable].includes(keywordArray[i - 1].nodeType))
              // eslint-disable-next-line no-param-reassign
              node.nodeType = NodeType.binary;
            else {
              // eslint-disable-next-line no-param-reassign
              node.nodeType = NodeType.unary;
              // eslint-disable-next-line no-param-reassign
              node.nodeValue = `u${node.nodeValue}`;
            }
          else if (typeof node.nodeValue === 'string' && node.nodeValue.toLowerCase() === 'null') {
            // eslint-disable-next-line no-param-reassign
            node.nodeType = NodeType.variable;
            // eslint-disable-next-line no-param-reassign
            node.nodeValue = null;
          } else {
            // eslint-disable-next-line no-param-reassign
            node.nodeType = NodeType.variable;
          }
          return node;
        }
        // eslint-disable-next-line no-param-reassign
        node.nodeType = NodeType.number;
        // eslint-disable-next-line no-param-reassign
        node.nodeValue = Number(node.nodeValue);
        if (Number.isNaN(node.nodeValue)) throw new Error(`${node.nodeValue} is an invalid number`);
        return node;
      }
      return node;
    });
  }

  /**
   * Extract the specified keyword and associate a node type to their nodes. In some cases, the extraction uses an optionally
   * regular expression.
   *
   * @param {FilterNodeType[]} FilterNodeArrayType - Array of keywords to process.
   * @param {string} keyword - Keyword to extract.
   * @param {RegExp} regExp - An optional regular expression to use for the extraction.
   *
   * @returns {FilterNodeType[]} The new keywords array.
   */
  static extractKeyword(filterNodeArray: FilterNodeType[], keyword: string, regExp?: RegExp): FilterNodeType[] {
    const getNodeType = (keywordValue: string): NodeType => {
      if (['+', '-'].includes(keywordValue)) return NodeType.unprocessedNode;
      if (binaryKeywors.includes(keywordValue)) return NodeType.binary;
      if (unaryKeywords.includes(keywordValue)) return NodeType.unary;
      if (groupKeywords.includes(keywordValue)) return NodeType.group;
      return NodeType.keyword;
    };

    return filterNodeArray.reduce((newKeywordArray, node) => {
      if (node.nodeType !== NodeType.unprocessedNode) newKeywordArray.push(node);
      else {
        // eslint-disable-next-line no-param-reassign
        newKeywordArray = newKeywordArray.concat(
          (node.nodeValue as string)
            .trim()
            .split(regExp === undefined ? keyword : regExp)
            .reduce((nodeArray, splitNode) => {
              if (splitNode === '') {
                nodeArray.push({ nodeType: getNodeType(keyword), nodeValue: keyword });
                return nodeArray;
              }
              nodeArray.push({ nodeType: NodeType.unprocessedNode, nodeValue: splitNode.trim() });
              nodeArray.push({ nodeType: getNodeType(keyword), nodeValue: keyword });
              return nodeArray;
            }, [] as FilterNodeType[])
            .slice(0, -1)
        );
      }
      return newKeywordArray;
    }, [] as FilterNodeType[]);
  }

  /**
   * Extract the string nodes from the keyword array. This operation is done at the beginning of the classification. This allows
   * to considere Keywords in a string as a normal word. If a problem is detected, an error object is thrown with an explanatory
   * message.
   *
   * @param {FilterNodeType[]} keywordArray - Array of keywords to process.
   *
   * @returns {FilterNodeType[]} The new keywords array with all string nodes classified.
   */
  static extractStrings(keywordArray: FilterNodeType[]): FilterNodeType[] {
    let stringNeeded = false;
    let stringHasBegun = false;
    let contiguousApostrophes = 0;
    let stringValue = '';
    const keywordArrayToReturn = keywordArray.reduce((newKeywordArray, node) => {
      if (stringHasBegun) {
        if (node.nodeType === NodeType.unprocessedNode) {
          if (stringNeeded) {
            stringValue = `${stringValue}${node.nodeValue}`;
            stringNeeded = false;
          } else {
            newKeywordArray.push({ nodeType: NodeType.string, nodeValue: stringValue });
            newKeywordArray.push(node);
            stringValue = '';
            stringHasBegun = false;
            stringNeeded = false;
            contiguousApostrophes = 0;
          }
        } else {
          contiguousApostrophes += 1;
          if (contiguousApostrophes === 2) {
            stringValue = `${stringValue}'`;
            stringNeeded = true;
            contiguousApostrophes = 0;
          }
        }
        return newKeywordArray;
      }
      if (node.nodeType === NodeType.keyword) {
        stringHasBegun = true;
        stringNeeded = true;
      } else newKeywordArray.push(node);
      return newKeywordArray;
    }, [] as FilterNodeType[]);
    if (stringHasBegun)
      if (!stringNeeded && contiguousApostrophes === 1) keywordArrayToReturn.push({ nodeType: NodeType.string, nodeValue: stringValue });
      else throw new Error(`string not closed`);
    return keywordArrayToReturn;
  }

  /**
   * Analyse the filter and split it in syntaxique nodes.  If a problem is detected, an error object is thrown with an
   * explanatory message.
   *
   * @param {FilterNodeType[]} filterNodeArrayType - Node array to analyse.
   *
   * @returns {FilterNodeType[]} The new node array with all nodes classified.
   */
  static analyzeLayerFilter(filterNodeArrayType: FilterNodeType[]): FilterNodeType[] {
    let resultingKeywordArray = filterNodeArrayType;
    resultingKeywordArray[0].nodeValue = (resultingKeywordArray[0].nodeValue as string).replaceAll(/\s{2,}/g, ' ').trim();
    resultingKeywordArray[0].nodeValue = resultingKeywordArray[0].nodeValue.split(/^date '|(?<=\s)date '/gi).join("date°'");
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, "'");
    resultingKeywordArray = this.extractStrings(resultingKeywordArray);

    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, '(');
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, ')');
    if (
      resultingKeywordArray.reduce((result, node) => {
        return node.nodeType === NodeType.group ? result + 1 : result;
      }, 0) % 2
    )
      throw new Error(`unbalanced parentheses`);

    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, 'date', /^date°$|^date°|(?<=\s)date°/g);
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, 'upper', /^upper\b|(?<=\s)upper\b/gi);
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, 'lower', /^lower\b|(?<=\s)lower\b/gi);
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, 'is not', /^is\s+not\b|(?<=\s)is\s+not\b/gi);
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, 'is', /^is\b(?!\s*not\b)|(?<=\s)is\b(?!\s*not\b)/gi);
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, 'in', /^in\b|(?<=\s)in\b/gi);
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, ',');
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, 'not', /^not\b|(?<=\s)not\b/gi);
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, 'and', /^and\b|(?<=\s)and\b/gi);
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, 'or', /^or\b|(?<=\s)or\b/gi);
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, 'like', /^like\b|(?<=\s)like\b/gi);
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, '=', /(?<![><])=/g);
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, '<', /<(?![>=])/g);
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, '>', /(?<!<)>(?!=)/g);
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, '<>');
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, '<=');
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, '>=');
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, '+');
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, '-');
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, '*');
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, '/');
    resultingKeywordArray = this.extractKeyword(resultingKeywordArray, '||');
    resultingKeywordArray = this.classifyUnprocessedNodes(resultingKeywordArray);

    return resultingKeywordArray;
  }

  /** Table used to define line symbology to use when drawing lineString and polygon perimeters */
  static lineDashSettings: Record<TypeLineStyle, number[] | undefined> = {
    dash: [16, 4],
    'dash-dot': [16, 4, 2, 4],
    'dash-dot-dot': [16, 4, 2, 4, 2, 4],
    dot: [2, 2],
    longDash: [25, 5],
    'longDash-dot': [25, 5, 2, 5],
    null: [0, 3],
    shortDash: [7, 3],
    'shortDash-dot': [7, 3, 2, 3],
    'shortDash-dot-dot': [7, 3, 2, 3, 2, 3],
    solid: undefined,
  };

  /** Table used to define line symbology to use when drawing polygon fill */
  static FillPatternSettings: FillPatternSettings = {
    null: [],
    dot: [],
    solid: [],
    backwardDiagonal: [
      { moveTo: [1, 0], lineTo: [2, 1] },
      { moveTo: [0, 0], lineTo: [2, 2] },
      { moveTo: [0, 1], lineTo: [1, 2] },
    ],
    cross: [
      { moveTo: [1, 0], lineTo: [1, 2] },
      { moveTo: [0, 1], lineTo: [2, 1] },
    ],
    diagonalCross: [
      { moveTo: [0, 0], lineTo: [2, 2] },
      { moveTo: [0, 2], lineTo: [2, 0] },
    ],
    forwardDiagonal: [
      { moveTo: [0, 1], lineTo: [1, 0] },
      { moveTo: [0, 2], lineTo: [2, 0] },
      { moveTo: [2, 1], lineTo: [1, 2] },
    ],
    horizontal: [{ moveTo: [0, 1], lineTo: [2, 1] }],
    vertical: [{ moveTo: [1, 0], lineTo: [1, 2] }],
  };

  /** Table of function to process simpleSymbol settings. */
  static #processSymbol: Record<TypeSymbol, (settings: TypeSimpleSymbolVectorConfig) => Style | undefined> = {
    circle: GeoviewRenderer.processCircleSymbol.bind(GeoviewRenderer),
    '+': GeoviewRenderer.processPlusSymbol.bind(GeoviewRenderer),
    diamond: GeoviewRenderer.processDiamondSymbol.bind(GeoviewRenderer),
    square: GeoviewRenderer.processSquareSymbol.bind(GeoviewRenderer),
    triangle: GeoviewRenderer.processTriangleSymbol.bind(GeoviewRenderer),
    X: GeoviewRenderer.processXSymbol.bind(GeoviewRenderer),
    star: GeoviewRenderer.processStarSymbol.bind(GeoviewRenderer),
  };

  /** Table of function to process polygon fill style settings. */
  static #processFillStyle: Record<TypeFillStyle, (settings: TypePolygonVectorConfig, geometry?: Geometry) => Style | undefined> = {
    null: GeoviewRenderer.processNullFill.bind(GeoviewRenderer),
    solid: GeoviewRenderer.processSolidFill.bind(GeoviewRenderer),
    backwardDiagonal: GeoviewRenderer.processBackwardDiagonalFill.bind(GeoviewRenderer),
    cross: GeoviewRenderer.processCrossFill.bind(GeoviewRenderer),
    diagonalCross: GeoviewRenderer.processDiagonalCrossFill.bind(GeoviewRenderer),
    forwardDiagonal: GeoviewRenderer.processForwardDiagonalFill.bind(GeoviewRenderer),
    horizontal: GeoviewRenderer.processHorizontalFill.bind(GeoviewRenderer),
    vertical: GeoviewRenderer.processVerticalFill.bind(GeoviewRenderer),
    dot: GeoviewRenderer.processDotFill.bind(GeoviewRenderer),
  };

  /** Table of function to process the style settings based on the feature geometry and the kind of style settings. */
  static processStyle: Record<TypeLayerStyleConfigType, Record<TypeStyleGeometry, TypeStyleProcessor>> = {
    simple: {
      Point: GeoviewRenderer.processSimplePoint.bind(GeoviewRenderer),
      MultiPoint: GeoviewRenderer.processSimplePoint.bind(GeoviewRenderer),
      LineString: GeoviewRenderer.processSimpleLineString.bind(GeoviewRenderer),
      MultiLineString: GeoviewRenderer.processSimpleLineString.bind(GeoviewRenderer),
      Polygon: GeoviewRenderer.processSimplePolygon.bind(GeoviewRenderer),
      MultiPolygon: GeoviewRenderer.processSimplePolygon.bind(GeoviewRenderer),
    },
    uniqueValue: {
      Point: GeoviewRenderer.processUniqueValuePoint.bind(GeoviewRenderer),
      MultiPoint: GeoviewRenderer.processUniqueValuePoint.bind(GeoviewRenderer),
      LineString: GeoviewRenderer.processUniqueLineString.bind(GeoviewRenderer),
      MultiLineString: GeoviewRenderer.processUniqueLineString.bind(GeoviewRenderer),
      Polygon: GeoviewRenderer.processUniquePolygon.bind(GeoviewRenderer),
      MultiPolygon: GeoviewRenderer.processUniquePolygon.bind(GeoviewRenderer),
    },
    classBreaks: {
      Point: GeoviewRenderer.processClassBreaksPoint.bind(GeoviewRenderer),
      MultiPoint: GeoviewRenderer.processClassBreaksPoint.bind(GeoviewRenderer),
      LineString: GeoviewRenderer.processClassBreaksLineString.bind(GeoviewRenderer),
      MultiLineString: GeoviewRenderer.processClassBreaksLineString.bind(GeoviewRenderer),
      Polygon: GeoviewRenderer.processClassBreaksPolygon.bind(GeoviewRenderer),
      MultiPolygon: GeoviewRenderer.processClassBreaksPolygon.bind(GeoviewRenderer),
    },
  };
} // END CLASS
